// composables/useConversationMonitor.js - FIXED VERSION
import { ref, onBeforeUnmount, readonly } from 'vue'
import { client } from '../lib/appwrite'
import { appwriteConfig } from '../lib/appwrite'
import { chatStateManager } from '../lib/chatStateManager'

export function useConversationMonitor() {
  const conversationStatus = ref('active');
  const statusSubscription = ref(null);
  const isMonitoringActive = ref(false); // ✅ Add active monitoring flag

  const startStatusMonitoring = (conversationId) => {
    if (statusSubscription.value) {
      statusSubscription.value(); // Cleanup previous subscription
    }

    // ✅ Set monitoring as active
    isMonitoringActive.value = true;
    console.log('📡 Setting monitoring as ACTIVE');

    try {
      const channelId = `databases.${appwriteConfig.databaseId}.collections.${appwriteConfig.conversationCollectionId}.documents.${conversationId}`;
      
      statusSubscription.value = client.subscribe(channelId, (response) => {
        // ✅ CRITICAL: Only process if monitoring is still active
        if (!isMonitoringActive.value) {
          console.log('📡 Ignoring status update - monitoring stopped');
          return;
        }
        
        console.log('📡 Conversation status update:', response);
        
        if (response.payload && response.payload.conversationStatus) {
          handleStatusChange(response.payload.conversationStatus, response.payload);
        }
      });
      
      console.log(`✅ Started status monitoring for: ${conversationId}`);
    } catch (error) {
      console.error('❌ Error starting status monitoring:', error);
    }
  };

  const handleStatusChange = (newStatus, conversationData) => {
    // ✅ CRITICAL: Don't process status changes if monitoring is stopped
    if (!isMonitoringActive.value) {
      console.log('📡 Ignoring status change - monitoring stopped');
      return;
    }
    
    const oldStatus = conversationStatus.value;
    conversationStatus.value = newStatus;
    
    console.log(`🔄 Status changed: ${oldStatus} → ${newStatus}`);

    // ✅ Generate system message based on status change
    let statusMessage = '';
    let shouldTransitionToEnd = false;
    
    switch (newStatus) {
      case 'paused':
        statusMessage = '⏸️ Chat wurde pausiert';
        break;
      case 'ended':
        statusMessage = '🔚 Chat wurde beendet';
        shouldTransitionToEnd = true;
        break;
      case 'active':
        if (oldStatus === 'paused') {
          statusMessage = '▶️ Chat wurde fortgesetzt';
        } else if (oldStatus === 'ended') {
          statusMessage = '🔄 Chat wurde wieder aktiviert';
        }
        break;
      case 'resolved':
        statusMessage = '✅ Chat wurde als gelöst markiert';
        shouldTransitionToEnd = true;
        break;
      case 'closed':
        statusMessage = '🔒 Chat wurde geschlossen';
        shouldTransitionToEnd = true;
        break;
    }

    // ✅ Add system message ONLY if there's a meaningful status change
    if (statusMessage && oldStatus !== newStatus) {
      // ✅ Check if we already have this status message to prevent duplicates
      const existingMessage = chatStateManager.getState().messages.find(msg => 
        msg.type === 'system' && 
        msg.content === statusMessage &&
        Math.abs(new Date() - new Date(msg.timestamp)) < 10000 // Within 10 seconds
      );
      
      if (!existingMessage) {
        const systemMessage = {
          id: `status-${Date.now()}-${Math.random()}`,
          type: 'system',
          content: statusMessage,
          timestamp: new Date(),
          statusChange: {
            from: oldStatus,
            to: newStatus,
            conversationId: conversationData.$id || 'unknown'
          }
        };

        // Add the system message directly to chat state
        chatStateManager.addMessage(systemMessage);
        console.log('💬 Added status system message:', statusMessage);
      } else {
        console.log('🚫 Status message already exists, skipping duplicate');
      }
    }

    // ✅ Handle phase transition if needed (but only if monitoring is active)
    if (shouldTransitionToEnd && isMonitoringActive.value) {
      setTimeout(() => {
        // ✅ Double-check monitoring is still active before transitioning
        if (isMonitoringActive.value) {
          console.log('🔄 Transitioning to end phase due to status change');
          chatStateManager.updateState({ currentPhase: 'end' });
        } else {
          console.log('📡 Skipping transition - monitoring was stopped');
        }
      }, 2000); // Small delay to let user read the message
    }

    return {
      statusMessage,
      shouldTransitionToEnd,
      oldStatus,
      newStatus
    };
  };

  const stopStatusMonitoring = () => {
    console.log('📡 Stopping status monitoring');
    
    // ✅ IMMEDIATELY set monitoring as inactive to prevent any processing
    isMonitoringActive.value = false;
    console.log('📡 Setting monitoring as INACTIVE');
    
    if (statusSubscription.value) {
      statusSubscription.value();
      statusSubscription.value = null;
      console.log('📡 Status monitoring subscription closed');
    }
  };

  // ✅ Enhanced cleanup
  onBeforeUnmount(() => {
    console.log('📡 useConversationMonitor unmounting - stopping monitoring');
    stopStatusMonitoring();
  });

  return {
    conversationStatus,
    startStatusMonitoring,
    stopStatusMonitoring,
    handleStatusChange,
    isMonitoringActive: readonly(isMonitoringActive) // ✅ Expose for debugging
  };
}